import React, { useEffect, useState } from 'react';
import { Scale, Plus, Loader2, Edit2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { LoanPolicy, LoanPolicyInput, policiesAPI } from '@/services/library-mgmt-api';
import LibButton from '@/components/librarian/ui/LibButton';

// The account types a policy can be written for. Mirrors LoanPolicy.USER_TYPE_CHOICES
// in backend/loans/models.py, values and labels; the backend rejects anything else.
const USER_TYPES: Array<{ value: string; label: string }> = [
  { value: 'student', label: 'Student' },
  { value: 'faculty', label: 'Faculty' },
  { value: 'staff', label: 'Staff' },
  { value: 'librarian', label: 'Librarian' },
  { value: 'admin', label: 'Administrator' },
  { value: 'guest', label: 'Guest' },
];

const labelFor = (v: string) => USER_TYPES.find((t) => t.value === v)?.label ?? v;

const PoliciesModule: React.FC = () => {
  const [items, setItems] = useState<LoanPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LoanPolicy | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await policiesAPI.list();
      setItems(data.results);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleDelete = async (p: LoanPolicy) => {
    if (!confirm(`Delete policy for "${labelFor(p.user_type)}"?`)) return;
    try {
      await policiesAPI.remove(p.id);
      toast.success('Policy deleted');
      void refresh();
    } catch { /* interceptor */ }
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)] flex items-center gap-2">
            <Scale className="h-6 w-6 text-[var(--lib-gold)]" />
            Loan Policies
          </h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Borrowing limits, fines and renewal rules per user type.</p>
        </div>
        <LibButton size="none"
          onClick={() => { setEditing(null); setShowForm(true); }} className="border border-[var(--lib-gold)] hover:brightness-110 font-medium text-sm rounded-lg px-4 py-2 transition-colors flex gap-2"
        >
          <Plus className="h-4 w-4" />
          New policy
        </LibButton>
      </div>

      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-[var(--lib-ink-muted)] text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-[var(--lib-ink-muted)]">No policies configured.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]">
                <Th>User type</Th>
                <Th className="text-right">Max books</Th>
                <Th className="text-right">Loan period</Th>
                <Th className="text-right">Renewals</Th>
                <Th className="text-right">Fine / day</Th>
                <Th className="text-right">Grace period</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--lib-hairline)]">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-[var(--lib-surface-2)] transition-colors">
                  <td className="px-5 py-3 font-medium text-[var(--lib-ink)]">{labelFor(p.user_type)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[var(--lib-ink-muted)]">{p.max_books}</td>
                  <td className="px-5 py-3 text-right font-mono text-[var(--lib-ink-muted)]">{p.loan_period_days} days</td>
                  <td className="px-5 py-3 text-right font-mono text-[var(--lib-ink-muted)]">{p.max_renewals}</td>
                  <td className="px-5 py-3 text-right font-mono text-[var(--lib-ink-muted)]">${parseFloat(p.fine_per_day).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[var(--lib-ink-muted)]">{p.grace_period_days} days</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => { setEditing(p); setShowForm(true); }}
                      className="inline-flex items-center gap-1 text-xs text-[var(--lib-navy)] hover:brightness-105 font-medium mr-3"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => void handleDelete(p)}
                      className="inline-flex items-center gap-1 text-xs text-[var(--lib-danger)] hover:brightness-105 font-medium"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PolicyForm
        open={showForm}
        editing={editing}
        usedTypes={items.map((p) => p.user_type)}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); void refresh(); }}
      />
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`text-left font-mono text-[10px] uppercase tracking-wider px-5 py-3 ${className}`}>
    {children}
  </th>
);

const PolicyForm: React.FC<{
  open: boolean;
  editing: LoanPolicy | null;
  usedTypes: string[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, usedTypes, onClose, onSaved }) => {
  const [form, setForm] = useState<LoanPolicyInput>({
    user_type: 'student',
    max_books: 5,
    loan_period_days: 14,
    max_renewals: 2,
    fine_per_day: 0.5,
    grace_period_days: 3,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        user_type: editing.user_type,
        max_books: editing.max_books,
        loan_period_days: editing.loan_period_days,
        max_renewals: editing.max_renewals,
        fine_per_day: parseFloat(editing.fine_per_day),
        grace_period_days: editing.grace_period_days,
      });
    } else {
      const firstAvailable = USER_TYPES.find((t) => !usedTypes.includes(t.value))?.value ?? 'student';
      setForm({
        user_type: firstAvailable,
        max_books: 5,
        loan_period_days: 14,
        max_renewals: 2,
        fine_per_day: 0.5,
        grace_period_days: 3,
      });
    }
  }, [editing, open, usedTypes]);

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await policiesAPI.update(editing.id, form);
        toast.success('Policy updated');
      } else {
        await policiesAPI.create(form);
        toast.success('Policy created');
      }
      onSaved();
    } catch { /* interceptor */ } finally {
      setSaving(false);
    }
  };

  const FF = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] block mb-1.5">{label}</label>
      {children}
    </div>
  );
  const inputCls = 'w-full h-9 px-3 rounded-md bg-transparent border border-[var(--lib-hairline)] text-[13px] focus:outline-none focus:border-[var(--lib-gold)]';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit policy' : 'New policy'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FF label="User type">
            <select
              value={form.user_type}
              onChange={(e) => setForm({ ...form, user_type: e.target.value })}
              disabled={!!editing}
              className={inputCls}
            >
              {USER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Max books">
              <input type="number" min={0} value={form.max_books} onChange={(e) => setForm({ ...form, max_books: Number(e.target.value) })} className={inputCls} />
            </FF>
            <FF label="Loan period (days)">
              <input type="number" min={1} value={form.loan_period_days} onChange={(e) => setForm({ ...form, loan_period_days: Number(e.target.value) })} className={inputCls} />
            </FF>
            <FF label="Max renewals">
              <input type="number" min={0} value={form.max_renewals} onChange={(e) => setForm({ ...form, max_renewals: Number(e.target.value) })} className={inputCls} />
            </FF>
            <FF label="Fine per day ($)">
              <input type="number" min={0} step={0.01} value={form.fine_per_day} onChange={(e) => setForm({ ...form, fine_per_day: Number(e.target.value) })} className={inputCls} />
            </FF>
            <FF label="Grace period (days)">
              <input type="number" min={0} value={form.grace_period_days} onChange={(e) => setForm({ ...form, grace_period_days: Number(e.target.value) })} className={inputCls} />
            </FF>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] flex items-center gap-1">
            <X className="h-4 w-4" /> Cancel
          </button>
          <LibButton size="none"
            onClick={submit}
            disabled={saving} className="px-4 py-2 text-sm font-medium rounded-lg transition-colors flex gap-1"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? 'Save changes' : 'Create'}
          </LibButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PoliciesModule;
